/* eslint-disable import/extensions */
/* eslint-disable @typescript-eslint/no-var-requires */
/* eslint-disable global-require */
import { PAGINATED_SIZE } from '@application/components/controllers/pages/ProListPageController/constants';
import {
  Button,
  CardItemPro,
  CardItemProContainer,
  CardItemProWrapper,
  EmptyOverlay,
  FiltersWithModal,
  Pagination,
  PaginationWrapper,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import { Form } from 'formik';
import type { FC } from 'react';
import { Suspense, lazy, useMemo } from 'react';
import { BiRefresh } from 'react-icons/bi';
import { FaMap } from 'react-icons/fa';
import type { ProListPageProps } from '..';
import { Filters } from './components/Filters';
import { HeaderSearch } from './components/HeaderSearch';
import { MapModal } from './components/MapModal';
import * as Styled from './styled';

export * from './types';

const Map = lazy(() => import('./components/Map').then((module) => ({ default: module.default })));

export const ProListPage: FC<ProListPageProps> = (props) => {
  const {
    items = [],
    unclaimedItems = [],
    pagination,
    loading = false,
    headerTitle,
    type,
    map,
    onResetClick,
    filtersModal,
    proSearch,
  } = props;

  const { translate } = useTranslation();

  const allItems = [...items, ...unclaimedItems];

  const unclaimedSubtitle = useMemo(() => {
    if (!allItems.length) return;
    if (!unclaimedItems.length) return;
    return translate(`proList.unclaimedSubtitle.${type}`);
  }, [allItems.length, translate, type, unclaimedItems.length]);

  const hasData = !!allItems.length;
  const hasMap = !!Map;

  return (
    <Form>
      <MapModal map={map} proSearch={proSearch} />
      <Styled.Wrapper hasMap={hasData && hasMap}>
        <HeaderSearch title={headerTitle} proSearch={proSearch} hasMap={hasMap} />

        <Styled.ContentWrapper>
          <Styled.ListWrapper hasMap={hasMap} mobileShown={!map.mobileShown}>
            <Styled.FiltersContainer>
              <FiltersWithModal {...filtersModal}>
                <Filters type={type} />
              </FiltersWithModal>
              <Styled.MapButton
                color="blue"
                outlined={map.mobileShown}
                iconLeft={<FaMap />}
                shrink
                onClick={map.onMobileShownToggle}
              >
                {translate('common.buttons.showMap')}
              </Styled.MapButton>
            </Styled.FiltersContainer>
            {loading ? (
              <Styled.ListCardContainerWrapper hasMap={hasMap}>
                {Array(PAGINATED_SIZE)
                  .fill(undefined)
                  .map((_, index) => (
                    <CardItemProWrapper key={index}>
                      <CardItemPro loading />
                    </CardItemProWrapper>
                  ))}
              </Styled.ListCardContainerWrapper>
            ) : (
              <Styled.ListCardContainerWrapper hasMap={hasMap}>
                {!hasData ? (
                  <EmptyOverlay>
                    <Button onClick={onResetClick} color="blue" shrink>
                      {translate('common.buttons.resetSearch')}
                    </Button>
                  </EmptyOverlay>
                ) : (
                  <CardItemProContainer>
                    {items.map((item, index) => (
                      <CardItemProWrapper key={index}>
                        <CardItemPro {...item} />
                      </CardItemProWrapper>
                    ))}
                  </CardItemProContainer>
                )}
                {!!unclaimedSubtitle && (
                  <Styled.UnclaimedSubtitle>{unclaimedSubtitle}</Styled.UnclaimedSubtitle>
                )}
                {!!unclaimedItems.length && (
                  <CardItemProContainer>
                    {unclaimedItems.map((item, index) => (
                      <CardItemProWrapper key={index}>
                        <CardItemPro {...item} />
                      </CardItemProWrapper>
                    ))}
                  </CardItemProContainer>
                )}
                <PaginationWrapper>
                  <Pagination marginPagesDisplayed={1} pageRangeDisplayed={2} {...pagination} />
                </PaginationWrapper>
              </Styled.ListCardContainerWrapper>
            )}
          </Styled.ListWrapper>
          {hasMap && (
            <>
              <Styled.MapWrapper mobileShown={map.mobileShown}>
                <Styled.MapStickyWrapper mobileShown={map.mobileShown}>
                  {(map.canRefresh || map.loading) && (
                    <Styled.MapRefreshButton
                      color="blue"
                      shrink
                      iconLeft={map.loading ? undefined : <BiRefresh />}
                      onClick={map.onRefreshClick}
                      loading={map.loading}
                    >
                      {translate('common.buttons.refreshMap')}
                    </Styled.MapRefreshButton>
                  )}
                  <Suspense fallback="...">
                    <Map {...map.props} />
                  </Suspense>
                </Styled.MapStickyWrapper>
              </Styled.MapWrapper>
            </>
          )}
        </Styled.ContentWrapper>
      </Styled.Wrapper>
    </Form>
  );
};
